<template>
    <transition name="backdrop">
        <div v-if="isOpen" class="m-container" :style="containerStyle">
            <div
                v-if="backdrop && isOpen"
                class="m-backdrop"
                :style="backdropStyle"
                @click.prevent="closeEnable ? close() : null"
            ></div>
            <slot name="content">
                <div v-if="isOpen" class="m-wrapper tw-shadow-md" :class="size" :style="wrapperStyle">
                    <div v-if="!headerless" class="m-header tw-select-none" :class="headerClass">
                        <i v-if="icon" class="tw-text-xl tw-mr-3 mdi" :class="'mdi-' + icon"></i>
                        <h4 class="!tw-font-normal">{{ title }}</h4>
                    </div>
                    <button v-if="closeEnable" class="m-close tw-relative tw-top-[1px]" @click="close()">
                        <i class="mdi mdi-close tw-text-2xl"></i>
                    </button>
                    <div
                        class="m-content"
                        :class="{
                            'is-paddingless': paddingless,
                            '!tw-rounded-b-lg': !$slots.footer,
                            '!tw-rounded-lg': headerless,
                        }"
                    >
                        <template v-if="loading">
                            <slot name="loading">
                                <LoadingSpinner />
                            </slot>
                        </template>
                        <slot v-else></slot>
                    </div>
                    <div class="m-footer" :class="footerClass">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </slot>
        </div>
    </transition>
</template>
<script>
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';
import { useOverlayZIndex } from '@/js/composables/useOverlayZIndex.ts';
import { useDataStore } from '@/js/stores/DataStore.js';

export default {
    components: {
        LoadingSpinner,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        icon: {
            type: String,
            default: null,
        },
        backdrop: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            required: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        paddingless: {
            type: Boolean,
            default: false,
        },
        headerless: {
            type: Boolean,
            default: false,
        },
        headerClass: {
            type: String,
            required: false,
        },
        footerClass: {
            type: String,
            required: false,
        },
        closeEnable: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['opened', 'closed', 'opening', 'closing'],
    data() {
        const store = useDataStore();
        return {
            isOpen: false,
            activeZIndex: null,
            store: store,
        };
    },
    computed: {
        containerStyle() {
            return this.activeZIndex ? { zIndex: this.activeZIndex } : null;
        },
        wrapperStyle() {
            return this.activeZIndex ? { zIndex: this.activeZIndex } : null;
        },
        backdropStyle() {
            return this.activeZIndex ? { zIndex: this.activeZIndex - 1 } : null;
        },
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.allocateZIndex();
                this.$emit('opened');
            } else {
                this.$emit('closed');
            }
        },
    },
    methods: {
        allocateZIndex() {
            const { activeZIndex, allocateZIndex } = useOverlayZIndex(); // returns new zIndex and function
            allocateZIndex();
            this.activeZIndex = activeZIndex.value;
        },
        open() {
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '15px';
            this.$emit('opening');
            this.isOpen = true;
        },
        close() {
            document.body.style.overflow = null;
            document.body.style.paddingRight = null;
            this.$emit('closing');
            this.isOpen = false;
        },
    },
};
</script>
<style lang="scss" scoped>
.m-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5500;

    & > .m-wrapper {
        position: relative;
        z-index: 5500;
        display: flex;
        flex-direction: column;
        width: min(calc(100vw - 30px), 520px);
        max-width: min(calc(100vw - 30px), 520px);
        border-radius: 12px;
        box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);

        &.is-small {
            width: min(calc(100vw - 30px), 450px);
            max-width: min(calc(100vw - 30px), 450px);
        }
        &.is-large {
            width: min(calc(100vw - 30px), 525px);
            max-width: min(calc(100vw - 30px), 525px);
        }
        &.is-xlarge {
            width: min(calc(100vw - 30px), 650px);
            max-width: min(calc(100vw - 30px), 650px);
        }
        &.is-xxlarge {
            width: min(calc(100vw - 30px), 800px);
            max-width: min(calc(100vw - 30px), 800px);
        }
        &.is-xxxlarge {
            width: min(calc(100vw - 30px), 960px);
            max-width: min(calc(100vw - 30px), 960px);
        }
        &.is-4xlarge {
            width: min(calc(100vw - 30px), 1600px);
            max-width: min(calc(100vw - 30px), 1600px);
        }
        &.is-fullwidth {
            width: calc(100vw - 100px);
            max-width: calc(100vw - 100px);
        }
        &.is-full {
            width: calc(100vw - 48px);
            max-width: calc(100vw - 48px);
        }

        & > .m-header:not(:empty) {
            background: #eaf0f9;
            padding: 16px 20px;
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
            display: flex;
            align-items: center;
            border-bottom: 1px solid rgba(0, 0, 0, 0.06);

            /* Ensure title and close button are vertically centered */
            & > h4 {
                margin: 0;
                display: flex;
                align-items: center;
                line-height: 1;
            }

            & > h4 {
                margin: 0;
                font-weight: 600;
                color: #1a1a1a;
                font-size: 18px;
                margin-top: 2px;
            }
        }

        & > .m-close {
            position: absolute;
            top: 7px;
            right: 7px;
            width: 40px;
            height: 40px;
            border: none;
            background: transparent;
            padding-top: 2px;
            margin-left: auto;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.15s ease;
            color: #6b7280;

            &:hover {
                background: rgba(0, 0, 0, 0.04);
                color: #1a1a1a;
            }
        }

        & > .m-content {
            background: white;
            padding: 24px;
            border-radius: 0;
            border: none;
            max-height: 80vh;
            overflow-y: auto;

            &.is-paddingless {
                padding: 0;
            }
        }

        & > .m-footer:not(:empty) {
            background: white;
            padding: 16px 24px;
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 12px;
            border-top: 1px solid rgba(0, 0, 0, 0.06);
        }
    }

    & > .m-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 5499;
        background: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
    }
}

.backdrop-enter-active,
.backdrop-leave-active {
    transition: all 0.2s ease;
    opacity: 1;
}
.backdrop-enter-active > .m-wrapper,
.backdrop-leave-active > .m-wrapper {
    transition: all 0.33s ease;
    transform: scale(1);
}
.backdrop-enter,
.backdrop-leave-to {
    opacity: 0;
}
.backdrop-enter > .m-wrapper,
.backdrop-leave-to > .m-wrapper {
    transform: scale(0.7);
}
</style>
